import { type TranslationIDs, useIntl } from '@capsicum/common';
import { Accordion, BoldText, Button, Text } from '@capsicum/common-web';
import {
	useV2FTMigrationAnalytics,
	useV2FTMigrationFAQScreen,
	type V2FTMigrationScreenProps
} from '@capsicum/features/nc-wallet/shared';
import { type PropsWithChildren, useCallback } from 'react';
import styled from 'styled-components';

import { useV2FTMigrationFAQUserQuits } from '../../hooks/useV2FTMigrationFAQUserQuits';

import { V2FTMigrationScreenContainer } from './V2FTMigrationScreenContainer';

export const V2FTMigrationFAQScreen = ({ changeScreen, tokens }: V2FTMigrationScreenProps) => {
	const { $t } = useIntl();
	const { openTransferFirstWarningModal } = useV2FTMigrationFAQUserQuits();
	const { triggerOptOutMigrationEvent } = useV2FTMigrationAnalytics({ tokens });

	const translateContent = useCallback(
		(translationID: TranslationIDs) =>
			$t<JSX.Element>(translationID, { bold: chunks => <BoldText>{chunks}</BoldText>, br: <br /> }),
		[$t]
	);

	const { questions } = useV2FTMigrationFAQScreen({
		Content: ({ children }: PropsWithChildren) => <S.FAQContent variant="text3">{children}</S.FAQContent>,
		Title: ({ children }: PropsWithChildren) => <Text variant="text1">{children}</Text>,
		translateContent
	});

	return (
		<V2FTMigrationScreenContainer
			footer={
				<S.Footer>
					<Button
						fullWidth
						testID="V2FTMigrationFAQScreen-migrate-button"
						text={$t('v2FTMigrationInitFlow.faq.actions.migrate')}
						onClick={() => changeScreen('MODAL')}
					/>

					<Button
						color="light"
						fullWidth
						testID="V2FTMigrationFAQScreen-delete-button"
						text={$t('v2FTMigrationInitFlow.faq.actions.delete')}
						variant="text"
						onClick={() => {
							triggerOptOutMigrationEvent();
							openTransferFirstWarningModal();
						}}
					/>
				</S.Footer>
			}
			title={$t('v2FTMigrationInitFlow.faq.title')}
			onBack={() => changeScreen('MODAL')}
		>
			<S.Content>
				<Accordion items={questions} />
			</S.Content>
		</V2FTMigrationScreenContainer>
	);
};

namespace S {
	export const Footer = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const FAQContent = styled(Text)`
		padding-top: ${({ theme }) => theme.spacings.small};
	`;

	export const Content = styled.div`
		${({ theme }) => theme.border.radius.medium};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
	`;
}
